import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getDocument } from '../redux/slices/documentSlice';
import DocumentPreview from '../components/templates/DocumentPreview';
import DocumentDownloadButtons from '../components/documents/DocumentDownloadButtons';
import { useHasPermission } from '../hooks/usePermission';
import ForbiddenPage from './ForbiddenPage';

/**
 * DocumentViewPage - View completed document page
 * 
 * Features:
 * - Display completed document content
 * - Loading and error states
 * - Back button navigation
 * - Permission-based access
 * 
 * Phase 6D: Document Management
 */
const DocumentViewPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { currentDocument, loading, error } = useSelector(
    (state) => state.documents
  );

  const hasPermission = useHasPermission('documents.view');

  useEffect(() => {
    if (id && hasPermission) {
      dispatch(getDocument(id));
    }
  }, [id, dispatch, hasPermission]);

  // Permission-based guard check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  // Handle back
  const handleBack = () => {
    history.goBack();
  };

  // Get document data (handle both nested and flat structure)
  const document = currentDocument?.document || currentDocument;

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {document?.title || 'Doküman Görüntüle'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tamamlanmış doküman içeriğini görüntüleyebilirsiniz
          </Typography>
        </Box>
        {document && (
          <DocumentDownloadButtons
            documentId={document._id || document.id}
            documentTitle={document.title}
            status={document.status}
          />
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && !document ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Doküman yükleniyor...
          </Typography>
        </Paper>
      ) : !document ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Doküman bulunamadı
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Doküman silinmiş veya erişim yetkiniz olmayabilir
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              height: 'calc(100vh - 300px)',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '3px',
                transition: 'background 0.2s ease',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.35)',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
            }}
          >
            {document.contentHtml ? (
              <DocumentPreview
                content={document.contentHtml}
                answers={document.answers || {}}
                questions={[]}
                status={document?.status || 'completed'}
                creditsUsed={document?.creditsUsed || 0}
                showOptionalPlaceholders={true}
              />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Doküman içeriği bulunamadı
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default DocumentViewPage;

